import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { exportCsv } from "@/lib/csv";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Line, LineChart, Legend } from "recharts";
import { FileDown, ChevronUp, ChevronDown, ChevronsUpDown, CalendarClock } from "lucide-react";
import { isRecruiting, displayStage as dispStage } from "@/lib/workspace";

const NAVY = "#15233B";
const GOLD = "#FBBF24";

function StatCard({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#15233B]/10 rounded-md p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">{label}</div>
      <div className="text-3xl font-extrabold tracking-tight mt-1" style={{ color: accent || NAVY }}>{value}</div>
      {sub && <div className="text-xs text-[#15233B]/60 mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, onExport, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#15233B]/10 rounded-md p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em]">{title}</h3>
        {onExport && (
          <button onClick={onExport} className="text-xs font-semibold text-[#FBBF24] hover:underline flex items-center gap-1">
            <FileDown className="h-3 w-3" /> CSV
          </button>
        )}
      </div>
      <div className="h-64 min-h-[260px]">{children}</div>
    </div>
  );
}

function SortTh({ label, col, sort, onSort, align }) {
  const active = sort.key === col;
  return (
    <th className={`py-1.5 px-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        data-testid={`partner-sort-${col}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.15em] hover:text-[#15233B] ${active ? "text-[#15233B]" : "text-[#15233B]/60"}`}
      >
        {label}
        {active ? (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );
}

function RecruitingReports() {
  const [data, setData] = useState(null);
  const [losts, setLosts] = useState([]);
  useEffect(() => {
    api.get("/reports/recruiting").then(({ data }) => setData(data)).catch(() => setData(false));
    api.get("/reports/lost-reasons", { params: { pipeline: "recruiting" } }).then(({ data }) => setLosts(data || [])).catch(() => setLosts([]));
  }, []);
  const funnel = (data?.funnel || []).map((f) => ({ ...f, label: `${dispStage(f.stage)}. ${f.name}` }));
  const sources = data?.sources || [];
  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Recruiting Reports</h1>
          <p className="text-sm text-[#15233B]/60">Time-to-hire, applicant sources and the hiring funnel.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard testId="recruit-active" label="Active candidates" value={data ? data.active : "—"} />
          <StatCard testId="recruit-hired" label="Hired" value={data ? data.hired : "—"} accent="#2E7D32" />
          <StatCard testId="recruit-tth" label="Avg time to hire" value={data?.avg_time_to_hire_days != null ? `${data.avg_time_to_hire_days}d` : "—"} sub="Application → Active Caregiver" accent={GOLD} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Hiring funnel — candidates per stage" testId="chart-recruit-funnel" onExport={() => exportCsv("avida-recruiting-funnel.csv", funnel.map(({ label, count }) => ({ stage: label, count })))}>
            <ResponsiveContainer>
              <BarChart data={funnel} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis type="number" stroke={NAVY} fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke={NAVY} fontSize={10} width={160} />
                <Tooltip />
                <Bar dataKey="count" name="Candidates" fill="#1E73C9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Applicant sources — where good caregivers come from" testId="chart-recruit-sources" onExport={() => exportCsv("avida-recruiting-sources.csv", sources)}>
            {sources.length === 0 ? (
              <div className="h-full grid place-items-center text-xs italic text-[#15233B]/40">
                No candidates yet — sources appear as applicants come in. Set each candidate's source in the card panel.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={sources}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                  <XAxis dataKey="source" stroke={NAVY} fontSize={11} />
                  <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" name="Applicants" fill={NAVY} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="hired" name="Hired" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {sources.length > 0 && (
          <ChartCard title="Source scorecard" testId="chart-recruit-source-table" onExport={() => exportCsv("avida-recruiting-sources.csv", sources)}>
            <div className="h-full overflow-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                  <tr className="border-b border-[#15233B]/10">
                    <th className="text-left py-1.5 pr-2">Source</th>
                    <th className="text-right py-1.5 px-2">Applicants</th>
                    <th className="text-right py-1.5 px-2">Hired</th>
                    <th className="text-right py-1.5 pl-2">Hire rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <tr key={s.source} data-testid={`recruit-source-row-${s.source}`} className="border-b border-[#15233B]/8">
                      <td className="py-2 pr-2 font-bold text-[#15233B]">{s.source}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{s.total}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#2E7D32] font-bold">{s.hired}</td>
                      <td className="py-2 pl-2 text-right tabular-nums">{s.hire_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        )}
        <ChartCard title="Archived candidates by reason" testId="chart-recruit-lost-reasons" onExport={() => exportCsv("avida-recruiting-archive-reasons.csv", losts)}>
          {losts.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#15233B]/50">No archived candidates yet.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={[...losts].sort((a, b) => b.count - a.count)}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis dataKey="reason" stroke={NAVY} fontSize={10} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#1E73C9" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
      <BrandFooter />
    </div>
  );
}

export default function ReportsPage() {
  if (isRecruiting()) return <RecruitingReports />;
  return <IntakeReports />;
}

function IntakeReports() {
  const [summary, setSummary] = useState(null);
  const [conv, setConv] = useState(null);
  const [stages, setStages] = useState([]);
  const [losts, setLosts] = useState([]);
  const [leadsMonth, setLeadsMonth] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [funnel, setFunnel] = useState({ funnel: [], biggest_drop: null });
  const [heatmap, setHeatmap] = useState([]);
  const [pct, setPct] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [marketing, setMarketing] = useState([]);
  const [partnerReport, setPartnerReport] = useState([]);
  const [acquisitions, setAcquisitions] = useState([]);
  const [partnerSort, setPartnerSort] = useState({ key: "total_leads", dir: "desc" });

  useEffect(() => {
    Promise.all([
      api.get("/reports/summary"),
      api.get("/reports/conversion"),
      api.get("/reports/avg-stage-days"),
      api.get("/reports/lost-reasons", { params: { pipeline: "intake" } }),
      api.get("/reports/leads-by-month"),
      api.get("/reports/weekly-trend", { params: { weeks: 12 } }),
      api.get("/reports/funnel"),
      api.get("/reports/aging-heatmap"),
      api.get("/reports/stage-percentiles"),
      api.get("/reports/owner-leaderboard", { params: { days: 14 } }),
      api.get("/reports/bottlenecks"),
      api.get("/reports/forecast"),
    ]).then(([s, c, st, lr, lm, wt, fn, hm, pp, lb, bn, fc]) => {
      setSummary(s.data); setConv(c.data); setStages(st.data); setLosts(lr.data); setLeadsMonth(lm.data); setWeekly(wt.data);
      setFunnel(fn.data); setHeatmap(hm.data); setPct(pp.data);
      setLeaderboard(lb.data); setBottlenecks(bn.data); setForecast(fc.data);
    });
    api.get("/reports/marketing-efforts").then(({ data }) => setMarketing(data || [])).catch(() => {});
    api.get("/reports/referral-partners").then(({ data }) => setPartnerReport(data || [])).catch(() => {});
    api.get("/reports/acquisitions").then(({ data }) => setAcquisitions(data || [])).catch(() => {});
  }, []);

  // Build leads-by-month chart data: sum across referral sources per month
  const leadsByMonthTotal = leadsMonth.map((row) => {
    const total = Object.entries(row).filter(([k]) => k !== "month").reduce((s, [, v]) => s + (Number(v) || 0), 0);
    return { month: row.month, leads: total, ...row };
  });

  const marketingChart = marketing.filter((m) => m.total_leads > 0);

  const togglePartnerSort = (key) =>
    setPartnerSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" || key === "category" ? "asc" : "desc" }
    );
  const sortedPartners = [...partnerReport].sort((a, b) => {
    const k = partnerSort.key;
    if (k === "name" || k === "category") {
      const av = (a[k] || "").toLowerCase();
      const bv = (b[k] || "").toLowerCase();
      return partnerSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    const av = Number(a[k]) || 0;
    const bv = Number(b[k]) || 0;
    return partnerSort.dir === "asc" ? av - bv : bv - av;
  });

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Reports</h1>
          <p className="text-sm text-[#15233B]/60">Agency pipeline snapshot.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testId="report-active" label="Active" value={summary?.active ?? "—"} />
          <StatCard testId="report-archived" label="Archived" value={summary?.archived ?? "—"} />
          <StatCard testId="report-conversion" label="Conversion" value={conv ? `${conv.conversion_rate}%` : "—"} sub={`${conv?.at_services_start || 0} reached Services Start`} accent={GOLD} />
          <StatCard testId="report-avg-days" label="Avg days to start" value={conv?.avg_days_to_services_start ?? "—"} sub="Lead → Services Start" />
        </div>

        {bottlenecks.length > 0 && (
          <div data-testid="bottleneck-banner" className="bg-[#FFCB05]/10 border border-[#FFCB05]/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/70">Stage bottlenecks</span>
              <span className="text-[10px] font-bold bg-[#FFCB05] text-[#15233B] px-1.5 py-0.5 rounded">{bottlenecks.length}</span>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {bottlenecks.slice(0, 6).map((b) => (
                <span key={b.stage_number} className="bg-white px-2 py-1 rounded border border-[#FFCB05]/40">
                  <span className="font-mono font-bold text-[#15233B]/55">{String(b.stage_number).padStart(2, "0")}</span>{" "}
                  <b className="text-[#15233B]">{b.name}</b> · p90 <b className="text-[#b91c1c]">{b.p90}d</b> vs target {b.target}d{" "}
                  <span className="text-[#15233B]/50">(+{b.overshoot}d)</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <ChartCard title="Owner leaderboard — last 14 days" testId="chart-owner-leaderboard" onExport={() => exportCsv("avida-owner-leaderboard.csv", leaderboard)}>
          <div className="h-full overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                <tr className="border-b border-[#15233B]/10">
                  <th className="text-left py-1.5 pr-2">Owner</th>
                  <th className="text-right py-1.5 px-2">Advances</th>
                  <th className="text-right py-1.5 px-2">Active</th>
                  <th className="text-right py-1.5 px-2">Overdue</th>
                  <th className="text-right py-1.5 px-2">Blocked</th>
                  <th className="text-right py-1.5 pl-2">Avg days</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((r) => (
                  <tr key={r.user_id} data-testid={`leaderboard-row-${r.user_id}`} className="border-b border-[#15233B]/8">
                    <td className="py-2 pr-2 font-bold text-[#15233B]">{r.name} <span className="text-[10px] text-[#15233B]/45 uppercase">{r.role}</span></td>
                    <td className="py-2 px-2 text-right font-extrabold text-[#1E73C9] tabular-nums">{r.advances}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.active}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-[#b91c1c]">{r.overdue}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-[#7e22ce]">{r.blocked}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">{r.avg_days_in_stage}d</td>
                  </tr>
                ))}
                {leaderboard.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-xs italic text-[#15233B]/40">No advances yet in this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <div data-testid="chart-marketing-efforts" className="bg-white border border-[#15233B]/10 rounded-md p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em]">Campaign ROI — which channels convert</h3>
              <p className="text-[11px] text-[#15233B]/55 mt-0.5">Leads sourced &amp; converted per event / campaign (Facebook, Google Ads, events…). Converted = reached Services Start.</p>
            </div>
            <button onClick={() => exportCsv("avida-campaign-roi.csv", marketing)} className="text-xs font-semibold text-[#FBBF24] hover:underline flex items-center gap-1 shrink-0">
              <FileDown className="h-3 w-3" /> CSV
            </button>
          </div>

          {marketingChart.length > 0 && (
            <div data-testid="marketing-roi-chart" className="h-56 mb-4">
              <ResponsiveContainer>
                <BarChart data={marketingChart} margin={{ left: 0, right: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                  <XAxis dataKey="name" stroke={NAVY} fontSize={10} interval={0} angle={-12} textAnchor="end" height={54} />
                  <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total_leads" name="Leads" fill={NAVY} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="converted_leads" name="Converted" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                <tr className="border-b border-[#15233B]/10">
                  <th className="text-left py-1.5 pr-2">Event / Campaign</th>
                  <th className="text-left py-1.5 px-2">Type</th>
                  <th className="text-left py-1.5 px-2">Date</th>
                  <th className="text-right py-1.5 px-2">Total leads</th>
                  <th className="text-right py-1.5 px-2">Active</th>
                  <th className="text-right py-1.5 px-2">Converted</th>
                  <th className="text-right py-1.5 px-2">Avg days→start</th>
                  <th className="text-right py-1.5 pl-2">Conv. rate</th>
                </tr>
              </thead>
              <tbody>
                {marketing.map((r, i) => {
                  const rate = Number(r.conversion_rate) || 0;
                  return (
                  <tr key={r.id || `u-${i}`} data-testid={`marketing-row-${i}`} className="border-b border-[#15233B]/8">
                    <td className="py-2 pr-2 font-bold text-[#15233B]">
                      {r.name}
                      {r.active === false && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#15233B]/40">(inactive)</span>}
                    </td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${r.type === "campaign" ? "bg-[#1E73C9]/10 text-[#1E73C9]" : "bg-[#FFCB05]/20 text-[#7a5a1a]"}`}>{r.type}</span>
                    </td>
                    <td className="py-2 px-2 text-[#15233B]/60 tabular-nums">{r.date || "—"}</td>
                    <td className="py-2 px-2 text-right font-extrabold text-[#1E73C9] tabular-nums">{r.total_leads}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.active_leads}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-[#2E7D32] font-bold">{r.converted_leads}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-[#15233B]/70" data-testid={`marketing-velocity-${i}`}>{r.avg_days_to_convert != null ? `${r.avg_days_to_convert}d` : "—"}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-16 bg-[#15233B]/10 rounded-full overflow-hidden hidden sm:block">
                          <div className="h-full bg-[#2E7D32] rounded-full" style={{ width: `${Math.min(100, rate)}%` }} />
                        </div>
                        <span className="font-bold text-[#15233B] w-11 text-right" data-testid={`marketing-conv-${i}`}>{rate}%</span>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {marketing.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-xs italic text-[#15233B]/40">No events or campaigns yet. Add them in Settings → Events &amp; Campaigns.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div data-testid="chart-referral-partners" className="bg-white border border-[#15233B]/10 rounded-md p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em]">Referral Partner Scorecard</h3>
              <p className="text-[11px] text-[#15233B]/55 mt-0.5">Ranked by performance · click any column to re-sort</p>
            </div>
            <button onClick={() => exportCsv("avida-referral-partner-scorecard.csv", sortedPartners)} className="text-xs font-semibold text-[#FBBF24] hover:underline flex items-center gap-1 shrink-0">
              <FileDown className="h-3 w-3" /> CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                <tr className="border-b border-[#15233B]/10">
                  <th className="text-center py-1.5 pr-2 w-10">#</th>
                  <SortTh label="Partner" col="name" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Category" col="category" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Total" col="total_leads" align="right" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Active" col="active_leads" align="right" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Converted" col="converted_leads" align="right" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Avg days→start" col="avg_days_to_convert" align="right" sort={partnerSort} onSort={togglePartnerSort} />
                  <SortTh label="Conv. rate" col="conversion_rate" align="right" sort={partnerSort} onSort={togglePartnerSort} />
                </tr>
              </thead>
              <tbody>
                {sortedPartners.map((r, i) => {
                  const medal = i === 0
                    ? "bg-[#FFCB05] text-[#15233B]"
                    : i === 1
                    ? "bg-[#15233B]/15 text-[#15233B]"
                    : i === 2
                    ? "bg-[#b8860b]/25 text-[#7a5a1a]"
                    : "text-[#15233B]/40";
                  const rate = Number(r.conversion_rate) || 0;
                  return (
                    <tr key={r.id || `p-${i}`} data-testid={`partner-report-row-${i}`} className="border-b border-[#15233B]/8">
                      <td className="py-2 pr-2 text-center">
                        <span className={`inline-grid place-items-center h-6 w-6 rounded-full text-[11px] font-extrabold ${medal}`}>{i + 1}</span>
                      </td>
                      <td className="py-2 px-2 font-bold text-[#15233B]">{r.name}{r.active === false && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#15233B]/40">(inactive)</span>}</td>
                      <td className="py-2 px-2 text-[#15233B]/70">{r.category}</td>
                      <td className="py-2 px-2 text-right font-extrabold text-[#1E73C9] tabular-nums">{r.total_leads}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.active_leads}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#2E7D32] font-bold">{r.converted_leads}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#15233B]/70" data-testid={`partner-velocity-${i}`}>{r.avg_days_to_convert != null ? `${r.avg_days_to_convert}d` : "—"}</td>
                      <td className="py-2 pl-2 text-right tabular-nums">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-16 bg-[#15233B]/10 rounded-full overflow-hidden hidden sm:block">
                            <div className="h-full bg-[#2E7D32] rounded-full" style={{ width: `${Math.min(100, rate)}%` }} />
                          </div>
                          <span className="font-bold text-[#15233B] w-11 text-right" data-testid={`partner-conv-${i}`}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {partnerReport.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-xs italic text-[#15233B]/40">No referral partners yet. Add them under the Partners tab.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ChartCard title="Acquisitions — leads sourced per person" testId="chart-acquisitions" onExport={() => exportCsv("avida-acquisitions.csv", acquisitions)}>
          <div className="h-full overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                <tr className="border-b border-[#15233B]/10">
                  <th className="text-left py-1.5 pr-2">Sourced by</th>
                  <th className="text-right py-1.5 px-2">Total leads</th>
                  <th className="text-right py-1.5 px-2">Active</th>
                  <th className="text-right py-1.5 pl-2">Converted</th>
                </tr>
              </thead>
              <tbody>
                {acquisitions.map((r, i) => (
                  <tr key={r.user_id || `a-${i}`} data-testid={`acq-row-${i}`} className="border-b border-[#15233B]/8">
                    <td className="py-2 pr-2 font-bold text-[#15233B]">{r.name} <span className="text-[10px] text-[#15233B]/45 uppercase">{r.role}</span></td>
                    <td className="py-2 px-2 text-right font-extrabold text-[#1E73C9] tabular-nums">{r.total_leads}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.active_leads}</td>
                    <td className="py-2 pl-2 text-right tabular-nums text-[#2E7D32] font-bold">{r.converted_leads}</td>
                  </tr>
                ))}
                {acquisitions.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-xs italic text-[#15233B]/40">No leads have a &quot;Sourced by&quot; set yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        {forecast && (
          <div data-testid="forecast-panel" className="bg-white border border-[#15233B]/10 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[#15233B]/10 bg-[#15233B] text-white flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCB05]">Forecast</div>
              <h3 className="text-sm font-extrabold tracking-tight">30 / 60 / 90 day projection</h3>
              <span className="ml-auto text-[10px] text-white/55">avg {forecast.avg_leads_per_week} leads/wk · conv {(forecast.conversion_rate * 100).toFixed(1)}%</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#15233B]/10">
              {forecast.projections.map((p) => (
                <div key={p.window} data-testid={`forecast-${p.weeks}w`} className="p-5 text-center">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/55">{p.window}</div>
                  <div className="mt-2 flex items-baseline justify-center gap-2">
                    <span className="text-3xl font-extrabold text-[#1E73C9] tabular-nums">{p.projected_leads}</span>
                    <span className="text-xs text-[#15233B]/55">projected leads</span>
                  </div>
                  <div className="mt-1 flex items-baseline justify-center gap-2">
                    <span className="text-xl font-extrabold text-[#2E7D32] tabular-nums">{p.projected_services_starts}</span>
                    <span className="text-xs text-[#15233B]/55">→ services starts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Stage funnel" testId="chart-funnel" onExport={() => exportCsv("avida-stage-funnel.csv", funnel.funnel)}>
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0">
                <ResponsiveContainer>
                  <BarChart data={funnel.funnel} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                    <XAxis type="number" stroke={NAVY} fontSize={11} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" stroke={NAVY} fontSize={10} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2563EB" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {funnel.biggest_drop && (
                <div className="text-[11px] text-[#15233B]/70 mt-2 border-t border-[#15233B]/10 pt-2">
                  Biggest drop: <b className="text-[#b91c1c]">{funnel.biggest_drop.from} → {funnel.biggest_drop.to}</b> ({funnel.biggest_drop.drop} cards)
                </div>
              )}
            </div>
          </ChartCard>

          <ChartCard title="Aging heatmap" testId="chart-aging-heatmap" onExport={() => exportCsv("avida-aging-heatmap.csv", heatmap.map((r) => ({ stage: r.name, target_days: r.target, ...r.buckets })))}>
            <div className="h-full overflow-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                    <th className="text-left py-1.5 pr-2">Stage</th>
                    {["0-3", "4-7", "8-14", "15+"].map((b) => (
                      <th key={b} className="px-1.5 py-1.5 text-center">{b}d</th>
                    ))}
                    <th className="text-right pl-2 py-1.5">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {heatmap.map((row) => {
                    const max = Math.max(1, ...Object.values(row.buckets));
                    return (
                      <tr key={row.stage_number} className="border-t border-[#15233B]/8">
                        <td className="py-1.5 pr-2 truncate max-w-[180px]">
                          <span className="font-mono text-[10px] text-[#15233B]/50 mr-1">{String(row.stage_number).padStart(2, "0")}</span>
                          <span className="font-semibold text-[#15233B]">{row.name}</span>
                        </td>
                        {["0-3", "4-7", "8-14", "15+"].map((b, bi) => {
                          const v = row.buckets[b] || 0;
                          const intensity = v / max;
                          const isLast = b === "15+";
                          const baseColor = isLast ? [185, 28, 28] : [185, 138, 46]; // red for 15+, gold otherwise
                          const bg = v === 0
                            ? "transparent"
                            : `rgba(${baseColor.join(",")},${0.12 + intensity * 0.78})`;
                          const fg = v === 0 ? "#15233B40" : (intensity > 0.4 ? "#fff" : "#15233B");
                          return (
                            <td
                              key={b}
                              className="px-1.5 py-1.5 text-center font-bold"
                              data-testid={`heatmap-${row.stage_number}-${bi}`}
                              style={{ background: bg, color: fg }}
                            >
                              {v || "·"}
                            </td>
                          );
                        })}
                        <td className="pl-2 py-1.5 text-right text-[#15233B]/60 font-mono text-[10px]">{row.target}d</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ChartCard>

          <ChartCard title="Cycle time — p50 / p90 (days per stage)" testId="chart-stage-percentiles" onExport={() => exportCsv("avida-stage-percentiles.csv", pct)}>
            <ResponsiveContainer>
              <BarChart data={pct} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis type="number" stroke={NAVY} fontSize={11} />
                <YAxis dataKey="name" type="category" stroke={NAVY} fontSize={10} width={140} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="p50" fill="#0D9488" radius={[0, 4, 4, 0]} name="p50 days" />
                <Bar dataKey="p90" fill="#FBBF24" radius={[0, 4, 4, 0]} name="p90 days" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Weekly trend (last 12 weeks)" testId="chart-weekly-trend" onExport={() => exportCsv("avida-weekly-trend.csv", weekly)}>
            <ResponsiveContainer>
              <LineChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis dataKey="week" stroke={NAVY} fontSize={11} />
                <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="active_total" stroke="#15233B" strokeWidth={3} dot={{ r: 3 }} name="Active total" />
                <Line type="monotone" dataKey="leads_in" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} name="Leads in" />
                <Line type="monotone" dataKey="advanced" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} name="Advanced" />
                <Line type="monotone" dataKey="archived" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} name="Archived" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Leads by month" testId="chart-leads-month" onExport={() => exportCsv("avida-leads-by-month.csv", leadsByMonthTotal)}>
            <ResponsiveContainer>
              <BarChart data={leadsByMonthTotal}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis dataKey="month" stroke={NAVY} fontSize={11} />
                <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="leads" fill={GOLD} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Avg days per stage" testId="chart-avg-stage" onExport={() => exportCsv("avida-avg-stage-days.csv", stages)}>
            <ResponsiveContainer>
              <BarChart data={stages} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis type="number" stroke={NAVY} fontSize={11} />
                <YAxis dataKey="name" type="category" stroke={NAVY} fontSize={10} width={140} />
                <Tooltip />
                <Bar dataKey="avg_days" fill={NAVY} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Lost / withdrawn by reason" testId="chart-lost-reasons" onExport={() => exportCsv("avida-lost-reasons.csv", losts)}>
            <ResponsiveContainer>
              <BarChart data={losts}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(21,35,59,0.08)" />
                <XAxis dataKey="reason" stroke={NAVY} fontSize={11} />
                <YAxis stroke={NAVY} fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {losts.map((_, i) => (
                    <Cell key={i} fill={["#b91c1c", "#7e22ce", "#FBBF24", "#166534", "#15233B", "#F59E0B"][i % 6]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Current overdue & blocked" testId="chart-overdue-blocked">
            <div className="h-full grid grid-cols-2 gap-3">
              <div className="bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md p-4 flex flex-col items-center justify-center">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#b91c1c]">Overdue</div>
                <div className="text-5xl font-extrabold text-[#b91c1c] mt-2">{summary?.overdue ?? 0}</div>
                <div className="text-xs text-[#15233B]/60 mt-1">Cards past stage target</div>
              </div>
              <div className="bg-[#7e22ce]/5 border border-[#7e22ce]/20 rounded-md p-4 flex flex-col items-center justify-center">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7e22ce]">Blocked</div>
                <div className="text-5xl font-extrabold text-[#7e22ce] mt-2">{summary?.blocked ?? 0}</div>
                <div className="text-xs text-[#15233B]/60 mt-1">Cards needing external action</div>
              </div>
            </div>
          </ChartCard>
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
